import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ApiAuthError,
  fetchQuotas,
  fetchUsers,
  fetchUsersDistribution,
  type DashboardWindow,
} from "../api/client";
import {
  EMPTY_USER_FILTERS,
  countActiveUserFilters,
  type UserFilters,
  type UserSortDir,
  type UserSortField,
  type UserSummary,
} from "../api/types";
import { SignInScreen } from "../auth/SignInScreen";
import { useToken } from "../auth/TokenContext";
import { CompletionRateHistogram } from "../components/charts/CompletionRateHistogram";
import { NewVsReturningUsersChart } from "../components/charts/NewVsReturningUsersChart";
import { SessionsPerUserHistogram } from "../components/charts/SessionsPerUserHistogram";
import { FiltersBar } from "../components/FiltersBar";
import { KpiCard } from "../components/KpiCard";
import { SessionDetailDrawer } from "../components/SessionDetailDrawer";
import { UserDetailDrawer } from "../components/UserDetailDrawer";
import { UsersFilterBar } from "../components/UsersFilterBar";
import { UsersTable } from "../components/UsersTable";
import { useFilters } from "../filters/FiltersContext";

const PAGE_SIZE = 25;
const QUERY_KEYS = ["users-distribution", "users", "user-detail"];

function formatNumber(n: number): string {
  return n.toLocaleString("en-US");
}

export function Users(): JSX.Element {
  const { token, signOut } = useToken();
  const { range, origin, granularity } = useFilters();

  const [offset, setOffset] = useState(0);
  const [sortBy, setSortBy] = useState<UserSortField>("total_cost");
  const [sortDir, setSortDir] = useState<UserSortDir>("desc");
  const [filters, setFilters] = useState<UserFilters>(EMPTY_USER_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  // When a user clicks a session inside the user drawer, stack the
  // session drawer on top so they can drill all the way down without
  // losing the user context.
  const [selectedSession, setSelectedSession] = useState<string | null>(null);

  const activeFilterCount = countActiveUserFilters(filters);
  const filterFingerprint = JSON.stringify(filters);
  const window: DashboardWindow = { from: range.from, to: range.to, origin };
  const tokenKey = token?.slice(-8);

  const distributionQuery = useQuery({
    queryKey: [
      "users-distribution",
      range.from,
      range.to,
      origin,
      granularity,
      tokenKey,
    ],
    queryFn: () => fetchUsersDistribution(window, token, granularity),
    enabled: !!token,
  });

  const usersQuery = useQuery({
    queryKey: [
      "users",
      range.from,
      range.to,
      origin,
      PAGE_SIZE,
      offset,
      sortBy,
      sortDir,
      filterFingerprint,
      tokenKey,
    ],
    queryFn: () =>
      fetchUsers(
        window,
        { limit: PAGE_SIZE, offset, sortBy, sortDir, filters },
        token,
      ),
    enabled: !!token,
  });

  const userIds = usersQuery.data?.users.map((u) => u.userId);
  const quotaQuery = useQuery({
    queryKey: ["quotas", userIds],
    queryFn: () => fetchQuotas(userIds!, token),
    enabled: !!userIds && userIds.length > 0 && !!token,
  });

  const allQueries = [distributionQuery, usersQuery];
  const authError = allQueries
    .map((q) => q.error)
    .find((e): e is ApiAuthError => e instanceof ApiAuthError);
  if (authError) {
    return (
      <SignInScreen
        reason={`Token rejected by the worker (HTTP ${authError.status}): ${authError.message}`}
      />
    );
  }

  const isRefreshing = allQueries.some((q) => q.isFetching);
  const distribution = distributionQuery.data;

  return (
    <section className="overview">
      <FiltersBar
        isRefreshing={isRefreshing}
        invalidateKeys={QUERY_KEYS}
        showGranularity
      />

      {allQueries.some((q) => q.isError) && !authError && (
        <div className="overview-error">
          Failed to load some data.{" "}
          <button type="button" onClick={signOut}>
            Sign out
          </button>
        </div>
      )}

      <div className="kpi-strip">
        <KpiCard
          label="Active users (window)"
          value={
            distribution ? formatNumber(distribution.kpis.activeUsers) : ""
          }
          delta={null}
          loading={distributionQuery.isLoading}
          hint="Distinct user_id over the window (ignores origin filter)"
        />
        <KpiCard
          label="Adobe"
          value={distribution ? formatNumber(distribution.kpis.adobe) : ""}
          delta={null}
          loading={distributionQuery.isLoading}
          hint="user_id ends with @adobe.com"
        />
        <KpiCard
          label="Non-Adobe"
          value={distribution ? formatNumber(distribution.kpis.nonAdobe) : ""}
          delta={null}
          loading={distributionQuery.isLoading}
          hint="Federated tenants (partners, customers)"
        />
      </div>

      <section className="chart-section">
        <header>
          <h2>New vs. returning users</h2>
          <p className="chart-subtitle">
            Stacked per bucket: <strong>new</strong> = the user's all-time first
            event (across the entire history) fell in this bucket;{" "}
            <strong>returning</strong>= the user was already active before. The
            split shows adoption health without confusing fresh signups with
            repeat traffic.
          </p>
        </header>
        <NewVsReturningUsersChart
          points={distribution?.newVsReturning ?? []}
          loading={distributionQuery.isLoading}
          granularity={granularity}
        />
      </section>

      <div className="chart-row-2col">
        <section className="chart-section">
          <header>
            <h2>Engagement distribution</h2>
            <p className="chart-subtitle">
              Users bucketed by their cone-aggregated session count in the
              window: one-shot (1), light (2–4), regular (5–20), heavy (21+). A
              long one-shot tail signals shallow adoption; a heavy bucket means
              power users.
            </p>
          </header>
          <SessionsPerUserHistogram
            entries={distribution?.sessionsHistogram ?? []}
            loading={distributionQuery.isLoading}
          />
        </section>

        <section className="chart-section">
          <header>
            <h2>Completion-rate distribution</h2>
            <p className="chart-subtitle">
              Per-user completion rate (completed v5 sessions / classified v5
              sessions). <code>no data</code> = no cone has a v5 classification
              yet — typically very-recent sessions or pre-Summit data.
            </p>
          </header>
          <CompletionRateHistogram
            entries={distribution?.completionHistogram ?? []}
            loading={distributionQuery.isLoading}
          />
        </section>
      </div>

      <section className="chart-section">
        <header>
          <h2>Users</h2>
          <p className="chart-subtitle">
            Sorted by cost desc by default. Click any row to open a per-user
            drawer with their token series, model mix, top skills/sprinkles, and
            recent sessions.
          </p>
        </header>
        <div className="sessions-filter-toggle-row">
          <button
            type="button"
            className={`sessions-filter-toggle${filtersOpen ? " is-open" : ""}`}
            onClick={() => setFiltersOpen((open) => !open)}
            aria-expanded={filtersOpen}
            aria-controls="users-filter-bar"
          >
            <span>{filtersOpen ? "▾" : "▸"} Filters</span>
            {activeFilterCount > 0 && (
              <span className="sessions-filter-toggle-badge">
                {activeFilterCount}
              </span>
            )}
          </button>
          {!filtersOpen && activeFilterCount > 0 && (
            <button
              type="button"
              className="filter-clear"
              onClick={() => {
                setFilters(EMPTY_USER_FILTERS);
                setOffset(0);
              }}
            >
              Clear filters
            </button>
          )}
        </div>
        {filtersOpen && (
          <div id="users-filter-bar">
            <UsersFilterBar
              filters={filters}
              onChange={(next) => {
                setFilters(next);
                setOffset(0);
              }}
            />
          </div>
        )}
        <UsersTable
          users={usersQuery.data?.users ?? []}
          total={usersQuery.data?.total ?? 0}
          limit={PAGE_SIZE}
          offset={offset}
          loading={usersQuery.isLoading}
          sortBy={sortBy}
          sortDir={sortDir}
          onSortChange={(nextBy, nextDir) => {
            setSortBy(nextBy);
            setSortDir(nextDir);
            setOffset(0);
          }}
          onSelect={(u: UserSummary) => setSelectedUser(u.userId)}
          onPageChange={(nextOffset) => setOffset(nextOffset)}
          quotas={quotaQuery.data?.quotas}
        />
      </section>

      <UserDetailDrawer
        userId={selectedUser}
        onClose={() => setSelectedUser(null)}
        onSelectSession={(sessionId) => setSelectedSession(sessionId)}
      />
      <SessionDetailDrawer
        sessionId={selectedSession}
        onClose={() => setSelectedSession(null)}
        onSelectUser={(userId) => {
          // Close the session drawer first so the user drawer
          // becomes the top-most layer — clicking the user_id pill
          // is essentially "back out to the user".
          setSelectedSession(null);
          setSelectedUser(userId);
        }}
      />
    </section>
  );
}
